import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { LdapGroupInfo, CreateLdapUserRequest, CreateLdapUserResponse } from 'shared';
import { ldapUsersApi, ldapErrorMessage } from '../../../api/ldapUsers';
import { WizardDraft } from '../LdapUserWizard';

interface ReviewStepProps {
  draft: WizardDraft;
  groups: LdapGroupInfo[];
  onBack: () => void;
  onCreated: (result: CreateLdapUserResponse) => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({ draft, groups, onBack, onCreated }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const orgGroups = draft.groups.filter((g) => groups.find((gi) => gi.cn === g)?.isOrgGroup);
  const roleGroups = draft.groups.filter((g) => !groups.find((gi) => gi.cn === g)?.isOrgGroup);

  const handleCreate = async () => {
    try {
      setSubmitting(true);
      setError('');
      const request: CreateLdapUserRequest = {
        uid: draft.uid,
        givenName: draft.givenName,
        sn: draft.sn,
        mail: draft.mail,
        groups: draft.groups,
        ...(draft.passwordMode === 'manual' && { password: draft.password }),
        ...(draft.gidNumber !== undefined && { gidNumber: draft.gidNumber }),
        ...(draft.loginShell && { loginShell: draft.loginShell }),
      };
      const result = await ldapUsersApi.create(request);
      onCreated(result);
    } catch (err: any) {
      setError(ldapErrorMessage(err, 'Failed to create LDAP user'));
    } finally {
      setSubmitting(false);
    }
  };

  const row = (label: string, value: React.ReactNode) => (
    <div className="flex justify-between py-1">
      <span className="text-gray-500 text-sm">{label}</span>
      <span className="text-sm font-medium text-right">{value}</span>
    </div>
  );

  return (
    <div data-component="ldap-wizard-review" className="flex flex-col gap-3">
      {error && <Message severity="error" text={error} className="w-full" />}

      <div className="border rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Identity</h4>
        {row('Username', <span className="font-mono">{draft.uid}</span>)}
        {row('Name', `${draft.givenName} ${draft.sn}`)}
        {row('Email', draft.mail)}
        <Divider className="my-2" />
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Groups</h4>
        {row(
          'Organization',
          orgGroups.length > 0 ? (
            <span className="flex gap-1 justify-end flex-wrap">
              {orgGroups.map((g) => (
                <Tag key={g} value={g} severity="info" />
              ))}
            </span>
          ) : (
            <span className="text-gray-400">none</span>
          )
        )}
        {row(
          'Roles / Functions',
          roleGroups.length > 0 ? roleGroups.join(', ') : <span className="text-gray-400">none</span>
        )}
        <Divider className="my-2" />
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Settings</h4>
        {row(
          'Password',
          draft.passwordMode === 'auto' ? 'Auto-generated (shown after creation)' : 'Set manually'
        )}
        {draft.templateUid &&
          row('Template', `${draft.templateUid} (groups + posix settings)`)}
        {draft.gidNumber !== undefined && row('gidNumber', draft.gidNumber)}
        {draft.loginShell && row('loginShell', <span className="font-mono">{draft.loginShell}</span>)}
      </div>

      <div className="flex justify-between pt-2">
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-button-text"
          onClick={onBack}
          disabled={submitting}
        />
        <Button
          label="Create User"
          icon="pi pi-check"
          className="p-button-success"
          loading={submitting}
          onClick={handleCreate}
        />
      </div>
    </div>
  );
};
